import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  BadgeCheck,
  Hash,
  UserRound,
  Users,
  Info,
} from "lucide-react";
import Swal from "sweetalert2";
import { UrlContext } from "../../contexts/UrlContext";

function ShareOption({
  checked,
  disabled = false,
  onChange,
  icon: Icon,
  title,
  description,
}) {
  return (
    <button
      type="button"
      className={[
        "group flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition duration-200",
        checked
          ? "border-[#365561]/30 bg-[#365561]/10 shadow-[0_8px_22px_rgba(54,85,97,0.08)]"
          : "border-slate-200/90 bg-white/85 hover:border-[#365561]/25 hover:bg-white",
        disabled
          ? "cursor-not-allowed opacity-45"
          : "cursor-pointer",
      ].join(" ")}
      onClick={() => {
        if (!disabled) {
          onChange(!checked);
        }
      }}
      disabled={disabled}
      aria-pressed={checked}
    >
      <span
        className={[
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition",
          checked
            ? "bg-[#365561] text-white"
            : "bg-[#365561]/10 text-[#365561]",
        ].join(" ")}
        aria-hidden="true"
      >
        <Icon size={18} strokeWidth={2} />
      </span>

      <span className="min-w-0 flex-1">
        <strong className="block text-[13px] font-semibold leading-[1.3] text-slate-800">
          {title}
        </strong>
        <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-slate-500">
          {description}
        </span>
      </span>

      <span
        className={[
          "relative h-[24px] w-[42px] shrink-0 rounded-full transition",
          checked ? "bg-[#365561]" : "bg-slate-300",
        ].join(" ")}
        aria-hidden="true"
      >
        <span
          className={[
            "absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-sm transition",
            checked ? "left-[21px]" : "left-[3px]",
          ].join(" ")}
        />
      </span>
    </button>
  );
}

export default function SharedInfoSelection({
  projectID,
  expertID,
  onDisplayIDChange,
  onIsShareNameChange,
}) {
  const { API_URL } = useContext(UrlContext);

  const [isShareName, setIsShareName] = useState("");
  const [openInfoSelection, setOpenInfoSelection] = useState(false);
  const [clientName, setClientName] = useState("");
  const [shareWithClient, setShareWithClient] = useState(false);
  const [displayID, setDisplayID] = useState(false);
  const [openClientShareAction, setOpenClientShareAction] = useState(false);

  const disableNameButton = displayID;
  const disableIdButton = isShareName;

  const handleClientShareAction = () => {
    setOpenClientShareAction(true);
  };

  const handleClientActionClose = () => {
    setShareWithClient((previous) => !previous);
    setOpenClientShareAction(false);
  };

  const saveShareName = (value) => {
    const url = `${API_URL}ppt-save-name-share`;

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        proj_id: projectID,
        exp_id: expertID,
        isShareName: value,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status !== "success") {
          console.log("error");
        }
      })
      .catch(() => {
        console.log("error while saving boolean data");
      });
  };

  const saveDisplayID = (value) => {
    const url = `${API_URL}ppt-save-display-ID`;

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        proj_id: projectID,
        exp_id: expertID,
        isDisplayID: value,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status !== "success") {
          console.log("error");
        }
      })
      .catch(() => {
        console.log("error while saving boolean data");
      });
  };

  const handleConfirmShare = () => {
    const url = `${API_URL}share-name-with-clients`;

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        projectID,
        clientName,
        isShared: shareWithClient,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setIsShareName(shareWithClient);
          setOpenClientShareAction(false);
        } else {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Something went wrong!",
            confirmButtonText: "OK",
          });
        }
      })
      .catch(() => {
        console.log("error while saving boolean data");

        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
          confirmButtonText: "OK",
        });
      });
  };

  const getInfo = () => {
    const url = `${API_URL}ppt-get-share-info`;

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        proj_id: projectID,
        exp_id: expertID,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setIsShareName(data.isShareName);
        setClientName(data.clientName);
        setShareWithClient(data.shareWithClient);
        setDisplayID(data.isDisplayID);
      });
  };

  useEffect(() => {
    getInfo();
  }, []);

  useEffect(() => {
    onDisplayIDChange(displayID);
    onIsShareNameChange(isShareName);
  }, [displayID, isShareName]);

  const selectionPopup =
    openInfoSelection && typeof document !== "undefined"
      ? createPortal(
          <AnimatePresence>
            <motion.div
              className="fixed inset-0 z-[10060] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm sm:p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16 }}
              role="presentation"
              data-pdp2-print-hide="true"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setOpenInfoSelection(false);
                }
              }}
            >
              <motion.div
                className="pdp2-section-picker flex max-h-[90vh] w-[min(780px,calc(100vw-32px))] flex-col overflow-hidden rounded-[26px] border border-slate-200/90 bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.14),transparent_34%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[0_28px_90px_rgba(15,23,42,0.34)]"
                initial={{ opacity: 0, y: 22, scale: 0.975 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{
                  type: "spring",
                  stiffness: 340,
                  damping: 30,
                  mass: 0.82,
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pdp2-share-info-title"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="pdp2-section-picker-header">
                  <div className="pdp2-section-picker-title-wrap">
                    <span className="pdp2-section-picker-eyebrow">
                      Client-facing identity
                    </span>

                    <strong id="pdp2-share-info-title">
                      Shared information
                    </strong>

                    <p>
                      Control how this consultant is identified when the
                      digital profile is shared with the client.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="pdp2-section-picker-close"
                    onClick={() => setOpenInfoSelection(false)}
                    aria-label="Close shared information"
                  >
                    ×
                  </button>
                </div>

                <div className="pdp2-section-picker-body">
                  <div className="space-y-4 p-4 sm:p-5">
                    <section className="rounded-2xl border border-slate-200/90 bg-white/70 p-4">
                      <div className="mb-3 flex items-start gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#365561]/10 text-[#365561]">
                          <Users size={17} strokeWidth={2} />
                        </span>

                        <div>
                          <strong className="block text-[13px] font-semibold text-slate-800">
                            Client-wide preference
                          </strong>
                          <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-slate-500">
                            Apply consultant-name sharing to all current and
                            future projects for{" "}
                            <strong className="font-semibold text-slate-700">
                              {clientName || "this client"}
                            </strong>
                            .
                          </span>
                        </div>
                      </div>

                      <ShareOption
                        checked={Boolean(shareWithClient)}
                        onChange={(value) => {
                          setShareWithClient(value);
                          handleClientShareAction();
                        }}
                        icon={BadgeCheck}
                        title={`Always share name with ${
                          clientName || "this client"
                        }`}
                        description="This preference applies across all current and future projects for the client."
                      />
                    </section>

                    <section className="rounded-2xl border border-slate-200/90 bg-white/70 p-4">
                      <div className="mb-3">
                        <strong className="block text-[13px] font-semibold text-slate-800">
                          This digital profile
                        </strong>
                        <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-slate-500">
                          Choose either the consultant name or the EPH ID for
                          this profile. These options are mutually exclusive.
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <ShareOption
                          checked={Boolean(isShareName)}
                          disabled={Boolean(disableNameButton)}
                          onChange={(value) => {
                            setIsShareName(value);
                            saveShareName(value);
                          }}
                          icon={UserRound}
                          title="Share consultant name"
                          description={
                            disableNameButton
                              ? "Turn off consultant ID first."
                              : "Display the consultant's name on the client-facing profile."
                          }
                        />

                        <ShareOption
                          checked={Boolean(displayID)}
                          disabled={Boolean(disableIdButton)}
                          onChange={(value) => {
                            setDisplayID(value);
                            saveDisplayID(value);
                          }}
                          icon={Hash}
                          title="Share consultant ID"
                          description={
                            disableIdButton
                              ? "Turn off consultant name first."
                              : "Display the EPH consultant ID instead of the name."
                          }
                        />
                      </div>
                    </section>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )
      : null;

  const confirmPopup =
    openClientShareAction && typeof document !== "undefined"
      ? createPortal(
          <AnimatePresence>
            <motion.div
              className="fixed inset-0 z-[10070] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm sm:p-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.14 }}
              role="presentation"
              data-pdp2-print-hide="true"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  handleClientActionClose();
                }
              }}
            >
              <motion.div
                className="w-[min(520px,calc(100vw-32px))] overflow-hidden rounded-[24px] border border-slate-200/90 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.34)]"
                initial={{ opacity: 0, y: 18, scale: 0.975 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 14, scale: 0.985 }}
                transition={{
                  type: "spring",
                  stiffness: 360,
                  damping: 30,
                  mass: 0.78,
                }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pdp2-share-confirm-title"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="p-5 sm:p-6">
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                      <AlertTriangle size={21} strokeWidth={2} />
                    </span>

                    <div className="min-w-0 flex-1">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#365561]">
                        Confirm client-wide change
                      </span>

                      <strong
                        id="pdp2-share-confirm-title"
                        className="mt-1 block text-[16px] font-semibold text-slate-900"
                      >
                        Update sharing preference?
                      </strong>

                      <p className="mt-2 text-[12px] leading-[1.55] text-slate-500">
                        All current and future projects under{" "}
                        <strong className="font-semibold text-slate-700">
                          {clientName || "this client"}
                        </strong>{" "}
                        will be affected by this change.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <button
                      type="button"
                      className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-[12px] font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none"
                      onClick={handleClientActionClose}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="rounded-xl bg-[#365561] px-4 py-2 text-[12px] font-semibold text-white transition hover:bg-[#2d4852] focus:outline-none"
                      onClick={handleConfirmShare}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )
      : null;

  return (
    <>
      <button
        type="button"
        className="pdp2-toolbar-button"
        onClick={() => setOpenInfoSelection(true)}
      >
        <Info size={18} strokeWidth={2} />
      </button>

      {selectionPopup}
      {confirmPopup}
    </>
  );
}